import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import AsistentesClasicosService from '../services/AsistentesClasicosService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import RegionalService from '../services/RegionalService';
import materiaService from '../services/MateriaService';
import tipoAsistenteService from '../services/TipoAsistenteService';
import { filterAdminMenuItems, isAdminArea, isAdminRegional } from '../utils/adminMenu';
import ProcesosMenu from './common/ProcesosMenu';
import './NewAssistant.css';

const VALIDATION_ERROR_MESSAGE = 'No pudimos procesar los archivos. Verifica que todos sean PDF válidos y que no superen los límites permitidos.';
const GPT_CLASSIC_MODELS = [
  'GPT-5.1 Instant',
  'GPT-5.1 Thinking',
  'GPT-5.2',
  'GPT-5.2 Instant',
  'GPT-5.2 Thinking'
];
const FEATURE_FLAG_NAME = 'AsistentesClasicos GPT';
const ADMIN_MENU_ITEMS = [
  { id: 'resumen', label: 'Dashboard', icon: 'fas fa-chart-line' },
  { id: 'profesores', label: 'Profesores', icon: 'fas fa-chalkboard-teacher' },
  // { id: 'alumnos', label: 'Alumnos', icon: 'fas fa-user-graduate' },
  { id: 'areas', label: 'Areas', icon: 'fas fa-sitemap' },
  { id: 'admin-area', label: 'Admins de área', icon: 'fas fa-user-tie' },
  { id: 'materias', label: 'Materias', icon: 'fas fa-book' },
  { id: 'asistentes', label: 'Asistentes OpenAI', icon: 'fas fa-robot' },
  { id: 'asistentes-gpt', label: 'Asistentes GPT', icon: 'fas fa-robot' }
];
const MODEL_DETAILS = {
  'GPT-5.1 Instant': {
    title: 'GPT-5.1 Instant',
    summary: 'Rápido y eficiente para respuestas inmediatas.',
    bestFor: 'Consultas cortas, respuestas rápidas y alto volumen.',
    notes: 'Equilibrio entre velocidad y calidad.'
  },
  'GPT-5.1 Thinking': {
    title: 'GPT-5.1 Thinking',
    summary: 'Más deliberativo para tareas que requieren razonamiento.',
    bestFor: 'Problemas complejos, análisis paso a paso.',
    notes: 'Puede demorar más en responder.'
  },
  'GPT-5.2': {
    title: 'GPT-5.2',
    summary: 'Mejor balance general de capacidad.',
    bestFor: 'Uso general, redacción y explicaciones detalladas.',
    notes: 'Buen punto medio entre calidad y costo.'
  },
  'GPT-5.2 Instant': {
    title: 'GPT-5.2 Instant',
    summary: 'Rendimiento rápido con mejoras de la serie 5.2.',
    bestFor: 'Respuestas ágiles con buena calidad.',
    notes: 'Ideal para flujos con alta concurrencia.'
  },
  'GPT-5.2 Thinking': {
    title: 'GPT-5.2 Thinking',
    summary: 'Mayor calidad en razonamiento y profundidad.',
    bestFor: 'Tareas exigentes, análisis y planificación.',
    notes: 'Mayor latencia, mejor precisión.'
  }
};

const getFriendlyApiErrorMessage = (error, fallback) => {
  if (!error || !error.message) {
    return fallback;
  }
  const normalized = error.message.toLowerCase();
  if (normalized.includes('validation')) {
    return VALIDATION_ERROR_MESSAGE;
  }
  return error.message;
};

const NewGptAssistant = ({ editAssistantId = null } = {}) => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const roleName = (user?.roleName || user?.nombreRol || user?.role || user?.rol || '').toLowerCase();
  const isAdminAreaUser = Boolean(user) && isAdminArea(user);
  const isAdminRegionalUser = Boolean(user) && isAdminRegional(user);
  const isAdminUser = Boolean(user) && (isAdminAreaUser || isAdminRegionalUser || roleName.includes('admin'));
  const isEditMode = Boolean(editAssistantId);
  const featureFlags = RegionalService.getFeatureFlags();
  const isGptClassicEnabled = featureFlags.some((flag) => {
    const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
    return nombre === FEATURE_FLAG_NAME.toLowerCase() && Boolean(flag?.activo ?? flag?.Activo);
  });
  const isGptClassicAllowed = Boolean(user) && (isGptClassicEnabled || isAdminArea(user));
  const filteredAdminMenuItems = filterAdminMenuItems(ADMIN_MENU_ITEMS, user)
    .filter((item) => isGptClassicEnabled || isAdminArea(user) || item.id !== 'asistentes-gpt');
  
  // Estados del componente
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAssistant, setIsLoadingAssistant] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [formData, setFormData] = useState({
    nombre: '',
    gptClasicoDescripcion: '',
    promptExtra: '',
    gptClasicoFraseIniciarConversacion: '',
    gptClasicoModelo: GPT_CLASSIC_MODELS[0],
    idRegional: '',
    idArea: '',
    idMateria: '',
    idTipoAsistente: '',
    archivosPdf: [],
    publico: true,
    passwordAsistente: '',
    gptClasicoBusquedaEnLaWeb: false,
    gptClasicoLienzo: true,
    gptClasicoGeneracionDeImagen: true,
    gptClasicoInterpreteCodigoYAnalisisDatos: true
  });
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [materiaFallback, setMateriaFallback] = useState(null);
  const [tiposAsistente, setTiposAsistente] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(false);
  const [tiposError, setTiposError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [modelInfo, setModelInfo] = useState(null);
  const [showModelModal, setShowModelModal] = useState(false);
  const [loadedAssistantId, setLoadedAssistantId] = useState(null);
  const [existingFiles, setExistingFiles] = useState([]);

  // Validar permisos al montar el componente
  useEffect(() => {
    if (authLoading) return;
    validateUserPermissions();
  }, [user, authLoading, isGptClassicEnabled]);

  const extractPromptExtra = (instructions) => {
    if (!instructions) return '';
    const marker = 'ACLARACIONES EXTRAS QUE DEBES CUMPLIR:';
    const idx = instructions.indexOf(marker);
    if (idx === -1) return '';
    return instructions.slice(idx + marker.length).trim();
  };

  const validateUserPermissions = () => {
    try {
      if (!isGptClassicEnabled && !isAdminArea(user)) {
        setHasPermission(false);
        setAlert({
          show: true,
          title: 'Funcionalidad deshabilitada',
          message: 'El feature flag de AsistentesClasicos GPT no está habilitado para esta regional.',
          type: 'warning'
        });
        setIsLoading(false);
        return;
      }

      // Verificar si hay usuario autenticado
      if (!user) {
        setAlert({
          show: true,
          title: 'Sesión requerida',
          message: 'Debes iniciar sesión para acceder a esta funcionalidad',
          type: 'error'
        });
        setHasPermission(false);
        setIsLoading(false);
        
        // Redirigir al login después de un breve retraso
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      setHasPermission(true);
      
    } catch (error) {
      console.error('Error al validar permisos:', error);
      setHasPermission(false);
      setAlert({
        show: true,
        title: 'Error de permisos',
        message: 'Error al validar permisos. Inténtalo nuevamente.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isGptClassicAllowed) return;
    const activeRegionalId = RegionalService.getActiveRegionalId();
    setFormData((prev) => ({ ...prev, idRegional: activeRegionalId || prev.idRegional }));

    const fetchMaterias = async () => {
      setMateriasLoading(true);
      setMateriasError('');
      try {
        let list = [];
        if (isAdminAreaUser) {
          list = activeRegionalId
            ? await materiaService.getMisMaterias({ idRegional: activeRegionalId })
            : await materiaService.getMisMaterias();
        } else if (isAdminUser) {
          list = activeRegionalId
            ? await materiaService.getMaterias({ idRegional: activeRegionalId })
            : await materiaService.getMaterias();
        } else {
          list = activeRegionalId
            ? await materiaService.getMisMaterias({ idRegional: activeRegionalId })
            : await materiaService.getMisMaterias();
        }
        setMaterias(Array.isArray(list) ? list : []);
      } catch (error) {
        setMateriasError(error.message || 'No pudimos cargar las materias.');
      } finally {
        setMateriasLoading(false);
      }
    };

    const fetchTipos = async () => {
      setTiposLoading(true);
      setTiposError('');
      try {
        const list = await tipoAsistenteService.getTiposActivos();
        setTiposAsistente(Array.isArray(list) ? list : []);
      } catch (error) {
        setTiposError(error.message || 'No pudimos cargar los tipos de asistente.');
      } finally {
        setTiposLoading(false);
      }
    };

    fetchMaterias();
    fetchTipos();
  }, [isGptClassicAllowed]);

  useEffect(() => {
    if (!isEditMode || !editAssistantId || !isGptClassicAllowed) return;
    if (loadedAssistantId === editAssistantId) return;

    const loadAssistant = async () => {
      setIsLoadingAssistant(true);
      try {
        const response = await AsistentesClasicosService.getAsistente(editAssistantId);
        const asistente =
          response?.asistente ||
          response?.Asistente ||
          response?.data?.asistente ||
          response?.data?.Asistente ||
          response;

        if (!asistente) {
          throw new Error('No se encontró el GPT');
        }

        const resolvedNombre =
          asistente.gptClasicoNombre ||
          asistente.GPTClasicoNombre ||
          asistente.nombre ||
          asistente.Nombre ||
          '';

        const resolvedDescripcion =
          asistente.gptClasicoDescripcion ||
          asistente.GPTClasicoDescripcion ||
          asistente.descripcion ||
          asistente.Descripcion ||
          '';

        const resolvedInstrucciones =
          asistente.gptClasicoInstrucciones ||
          asistente.GPTClasicoInstrucciones ||
          '';

        const resolvedMateriaId = asistente.idMateria || asistente.IdMateria || '';
        const resolvedMateriaNombre = asistente.nombreMateria || asistente.NombreMateria || '';
        const resolvedMateriaArea = asistente.idArea || asistente.IdArea || '';
        const resolvedMateriaRegional = asistente.idRegional || asistente.IdRegional || '';

        setFormData((prev) => ({
          ...prev,
          nombre: resolvedNombre,
          gptClasicoDescripcion: resolvedDescripcion,
          promptExtra: extractPromptExtra(resolvedInstrucciones),
          gptClasicoFraseIniciarConversacion:
            asistente.gptClasicoFraseIniciarConversacion ||
            asistente.GPTClasicoFraseIniciarConversacion ||
            '',
          gptClasicoModelo:
            asistente.gptClasicoModelo ||
            asistente.GPTClasicoModelo ||
            prev.gptClasicoModelo,
          idRegional: resolvedMateriaRegional || prev.idRegional,
          idArea: resolvedMateriaArea || '',
          idMateria: resolvedMateriaId,
          idTipoAsistente: asistente.idTipoAsistente || asistente.IdTipoAsistente || prev.idTipoAsistente,
          publico: asistente.publico ?? asistente.Publico ?? true,
          passwordAsistente: asistente.passwordAsistente || asistente.PasswordAsistente || '',
          gptClasicoBusquedaEnLaWeb:
            asistente.gptClasicoBusquedaEnLaWeb ??
            asistente.GPTClasicoBusquedaEnLaWeb ??
            prev.gptClasicoBusquedaEnLaWeb,
          gptClasicoLienzo:
            asistente.gptClasicoLienzo ??
            asistente.GPTClasicoLienzo ??
            prev.gptClasicoLienzo,
          gptClasicoGeneracionDeImagen:
            asistente.gptClasicoGeneracionDeImagen ??
            asistente.GPTClasicoGeneracionDeImagen ??
            prev.gptClasicoGeneracionDeImagen,
          gptClasicoInterpreteCodigoYAnalisisDatos:
            asistente.gptClasicoInterpreteCodigoYAnalisisDatos ??
            asistente.GPTClasicoInterpreteCodigoYAnalisisDatos ??
            prev.gptClasicoInterpreteCodigoYAnalisisDatos
        }));

        if (resolvedMateriaId && resolvedMateriaNombre) {
          setMateriaFallback({
            Id: resolvedMateriaId,
            Nombre: resolvedMateriaNombre,
            IdArea: resolvedMateriaArea || null,
            IdRegional: resolvedMateriaRegional || null
          });
        } else {
          setMateriaFallback(null);
        }

        const archivos = asistente.archivos || asistente.Archivos || [];
        setExistingFiles(Array.isArray(archivos) ? archivos : []);

        setLoadedAssistantId(editAssistantId);
      } catch (error) {
        setAlert({
          show: true,
          title: 'Error al cargar el GPT',
          message: getFriendlyApiErrorMessage(error, 'No pudimos obtener los datos del GPT Clásico. Inténtalo nuevamente.'),
          type: 'error'
        });
      } finally {
        setIsLoadingAssistant(false);
      }
    };

    loadAssistant();
  }, [isEditMode, editAssistantId, isGptClassicAllowed, loadedAssistantId]);

  useEffect(() => {
    if (!materiaFallback || !materiaFallback.Id) return;
    const exists = materias.some((m) => (m.id || m.Id) === materiaFallback.Id);
    if (!exists) {
      setMaterias((prev) => [materiaFallback, ...prev]);
    }
  }, [materiaFallback, materias]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModelChange = (value) => {
    handleInputChange('gptClasicoModelo', value);
    const details = MODEL_DETAILS[value];
    if (details) {
      setModelInfo(details);
      setShowModelModal(true);
    }
  };

  const closeModelModal = () => {
    setShowModelModal(false);
  };

  const handleMateriaChange = (value) => {
    const selected = materias.find((m) => (m.id || m.Id) === value);
    const idArea = selected?.idArea || selected?.IdArea || '';
    const idRegional = selected?.idRegional || selected?.IdRegional || formData.idRegional || '';
    setFormData((prev) => ({
      ...prev,
      idMateria: value,
      idArea,
      idRegional
    }));
  };

  // Manejar drag and drop de archivos
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (newFiles) => {
    // Filtrar solo archivos PDF
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== newFiles.length) {
      setAlert({
        show: true,
        title: 'Archivos inválidos',
        message: 'Solo se permiten archivos PDF',
        type: 'warning'
      });
    }

    setFormData(prev => {
      const currentFiles = prev.archivosPdf;
      const totalFiles = [...currentFiles, ...pdfFiles];
      
      // Validar máximo 20 archivos
      if (totalFiles.length > 20) {
        setAlert({
          show: true,
          title: 'Límite de archivos',
          message: 'No se pueden subir más de 20 archivos PDF',
          type: 'error'
        });
        return prev; // No actualizar si excedel límite
      }
      
      return {
        ...prev,
        archivosPdf: totalFiles
      };
    });
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      archivosPdf: prev.archivosPdf.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.nombre.trim()) {
      setAlert({
        show: true,
        title: 'Nombre requerido',
        message: 'El nombre del asistente es obligatorio',
        type: 'error'
      });
      return;
    }

    if (materiasLoading) {
      setAlert({
        show: true,
        title: 'Materias cargando',
        message: 'Esperá a que carguen tus materias para continuar.',
        type: 'warning'
      });
      return;
    }

    if (materias.length === 0) {
      setAlert({
        show: true,
        title: 'Sin materias asignadas',
        message: 'No tienes materias asignadas para asociar al asistente.',
        type: 'warning'
      });
      return;
    }

    if (!formData.idMateria) {
      setAlert({
        show: true,
        title: 'Materia requerida',
        message: 'Selecciona una materia asignada para el asistente.',
        type: 'warning'
      });
      return;
    }

    if (!isEditMode && formData.archivosPdf.length === 0) {
      setAlert({
        show: true,
        title: 'Archivos requeridos',
        message: 'Debes subir al menos un archivo PDF',
        type: 'error'
      });
      return;
    }

    if (formData.archivosPdf.length > 20) {
      setAlert({
        show: true,
        title: 'Límite de archivos',
        message: 'No puedes subir más de 20 archivos PDF',
        type: 'error'
      });
      return;
    }
    if (!formData.publico && !formData.passwordAsistente.trim()) {
      setAlert({
        show: true,
        title: 'Password requerido',
        message: 'Debes ingresar un password cuando el GPT es privado.',
        type: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Mostrar mensaje inicial
      setAlert({
        show: true,
        title: isEditMode ? 'Actualizando GPT Clásico' : 'Creando GPT Clásico',
        message: isEditMode
          ? 'Actualizando GPT Clásico... Este proceso puede demorar unos minutos debido a los archivos que se deben procesar.'
          : 'Creando GPT Clásico... Este proceso puede demorar unos minutos debido a los archivos que se deben procesar.',
        type: 'info'
      });
      
      // Crear FormData para envío multipart
      const formDataToSend = new FormData();
      formDataToSend.append('nombre', formData.nombre);
      formDataToSend.append('gptClasicoNombre', formData.nombre);
      if (formData.gptClasicoDescripcion.trim()) {
        formDataToSend.append('gptClasicoDescripcion', formData.gptClasicoDescripcion.trim());
      }
      if (formData.idRegional) {
        formDataToSend.append('idRegional', formData.idRegional);
      }
      if (formData.idArea) {
        formDataToSend.append('idArea', formData.idArea);
      }
      if (formData.idMateria) {
        formDataToSend.append('idMateria', formData.idMateria);
      }
      if (formData.idTipoAsistente) {
        formDataToSend.append('idTipoAsistente', formData.idTipoAsistente);
      }
      
      // Agregar todos los archivos PDF
      formData.archivosPdf.forEach(file => {
        formDataToSend.append('archivosPdf', file);
      });
      if (formData.promptExtra.trim()) {
        if (isEditMode) {
          formDataToSend.append('gptClasicoInstrucciones', formData.promptExtra.trim());
        } else {
          formDataToSend.append('promptExtra', formData.promptExtra.trim());
        }
      }
      if (formData.gptClasicoFraseIniciarConversacion.trim()) {
        formDataToSend.append('gptClasicoFraseIniciarConversacion', formData.gptClasicoFraseIniciarConversacion.trim());
      }
      if (formData.gptClasicoModelo) {
        formDataToSend.append('gptClasicoModelo', formData.gptClasicoModelo);
      }
      formDataToSend.append('gptClasicoBusquedaEnLaWeb', String(formData.gptClasicoBusquedaEnLaWeb));
      formDataToSend.append('gptClasicoLienzo', String(formData.gptClasicoLienzo));
      formDataToSend.append('gptClasicoGeneracionDeImagen', String(formData.gptClasicoGeneracionDeImagen));
      formDataToSend.append('gptClasicoInterpreteCodigoYAnalisisDatos', String(formData.gptClasicoInterpreteCodigoYAnalisisDatos));
      formDataToSend.append('publico', String(formData.publico));
      formDataToSend.append('conPassword', String(!formData.publico));
      if (!formData.publico && formData.passwordAsistente.trim()) {
        formDataToSend.append('passwordAsistente', formData.passwordAsistente.trim());
      }

      // Hacer la petición POST/PUT
      if (isEditMode) {
        await AsistentesClasicosService.actualizarAsistente(editAssistantId, formDataToSend);
      } else {
        await AsistentesClasicosService.crearAsistente(formDataToSend);
      }
      
      setAlert({
        show: true,
        title: isEditMode ? 'GPT Clásico actualizado' : 'GPT Clásico creado',
        message: isEditMode ? 'GPT Clásico actualizado correctamente.' : 'GPT Clásico creado exitosamente',
        type: 'success'
      });
      
      // Limpiar formulario después del éxito
      setTimeout(() => {
        if (!isEditMode) {
          setFormData({
            nombre: '',
            gptClasicoDescripcion: '',
            promptExtra: '',
            gptClasicoFraseIniciarConversacion: '',
            gptClasicoModelo: GPT_CLASSIC_MODELS[0],
            idRegional: RegionalService.getActiveRegionalId() || '',
            idArea: '',
            idMateria: '',
            idTipoAsistente: '',
            archivosPdf: [],
            publico: true,
            passwordAsistente: '',
            gptClasicoBusquedaEnLaWeb: false,
            gptClasicoLienzo: true,
            gptClasicoGeneracionDeImagen: true,
            gptClasicoInterpreteCodigoYAnalisisDatos: true
          });
        }
        navigate('/gpt-assistants');
      }, 1500);
      
    } catch (error) {
      console.error(isEditMode ? '❌ Error al actualizar asistente:' : '❌ Error al crear asistente:', error);
      setAlert({
        show: true,
        title: isEditMode ? 'Error al actualizar asistente' : 'Error al crear asistente',
        message: getFriendlyApiErrorMessage(
          error,
          isEditMode ? 'No pudimos actualizar el asistente. Inténtalo nuevamente.' : 'No pudimos crear el asistente. Inténtalo nuevamente.'
        ),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const handleAdminNavigate = (itemId) => {
    if (itemId === 'asistentes-gpt') {
      navigate('/gpt-assistants');
      return;
    }
    navigate('/admin-dashboard', { state: { tab: itemId } });
  };

  const handleGoBack = () => {
    navigate(isEditMode ? '/gpt-assistants' : '/dashboard');
  };

  const handleLogout = () => {
    logout();
  };

  // Mostrar loading mientras se validan permisos
  if (authLoading || isLoading || isLoadingAssistant) {
    return (
      <div className="new-assistant-container">
        <div className="loading-container">
          <LoadingSpinner message={isLoadingAssistant ? 'Cargando GPT...' : 'Validando permisos...'} />
        </div>
      </div>
    );
  }

  // Redirigir al dashboard si no tiene permisos (mostrar alerta allí)
  if (!hasPermission) {
    navigate('/dashboard', { replace: true, state: { deniedCreateAssistant: true } });
    return null;
  }

  // Mostrar formulario de creación si tiene permisos
  return (
    <div className={`new-dashboard ${isAdminUser ? 'admin-mode' : ''}`}>
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {isAdminUser ? (
            filteredAdminMenuItems.map((item) => (
              <button
                key={item.id}
                className={`nav-item ${item.id === 'asistentes-gpt' ? 'active' : ''}`}
                onClick={() => handleAdminNavigate(item.id)}
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
              </button>
            ))
          ) : (
            <>
              <button
                className="nav-item"
                onClick={() => navigate('/dashboard')}
              >
                <i className="fas fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </button>
              <button className="nav-item active">
                <i className="fas fa-plus"></i>
                <span>{isEditMode ? 'Editar GPT Clásico' : 'Nuevo GPT Clásico'}</span>
              </button>
              <button
                className="nav-item"
                onClick={() => navigate('/gpt-assistants')}
              >
                <i className="fas fa-list-ul"></i>
                <span>Asistentes GPT</span>
              </button>
              <button
                className="nav-item"
                onClick={() => navigate('/reports')}
              >
                <i className="fas fa-chart-bar"></i>
                <span>Reportes</span>
              </button>
            </>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || user?.roleName || user?.nombreRol || 'Usuario'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">
              <i className="fas fa-robot"></i>
              {isEditMode ? 'Editar GPT Clásico' : 'Crear GPT Clásico'}
            </h1>
            <p className="page-subtitle">
              {isEditMode
                ? 'Actualiza la información y archivos del GPT Clásico'
                : 'Sube archivos PDF para crear un GPT Clásico personalizado'}
            </p>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <ProcesosMenu />
            <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
            
            <div className="user-menu">
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                    <span className="user-email">{user?.email || 'usuario@utn.edu.ar'}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item logout-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          <div className="new-assistant-content">
            <form onSubmit={handleSubmit} className="assistant-form">
              <div className="assistant-form-grid">
                <div className="assistant-form-panel">
              {/* Información básica */}
              <div className="form-section basic-info-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Información del GPT Clásico</h3>
                    <p className="section-description">Define el nombre y características básicas de tu GPT Clásico</p>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="nombre" className="form-label">
                    <i className="fas fa-tag"></i>
                    Nombre del GPT Clásico *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    className="form-input enhanced"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Ej: GPT Clásico de Lógica y Estructuras Discretas"
                    required
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-lightbulb"></i>
                    Escoge un nombre descriptivo que refleje el área de conocimiento
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="gptClasicoDescripcion" className="form-label">
                    <i className="fas fa-align-left"></i>
                    Descripción del GPT Clásico
                  </label>
                  <textarea
                    id="gptClasicoDescripcion"
                    className="form-input enhanced"
                    rows="3"
                    value={formData.gptClasicoDescripcion}
                    onChange={(e) => handleInputChange('gptClasicoDescripcion', e.target.value)}
                    placeholder="Ej: Asistente especializado en Lógica y Estructuras Discretas para alumnos de primer año"
                    disabled={isSubmitting}
                  />
                  <div className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Breve descripción que verán los usuarios del GPT Clásico
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="idTipoAsistente" className="form-label">
                    <i className="fas fa-layer-group"></i>
                    Tipo de asistente
                  </label>
                  <select
                    id="idTipoAsistente"
                    className="form-select enhanced"
                    value={formData.idTipoAsistente}
                    onChange={(e) => handleInputChange('idTipoAsistente', e.target.value)}
                    disabled={isSubmitting || tiposLoading}
                  >
                    <option value="">Selecciona un tipo</option>
                    {tiposAsistente.map((tipo) => {
                      const id = tipo.id || tipo.Id;
                      const nombre = tipo.nombre || tipo.Nombre || 'Tipo';
                      return (
                        <option key={id} value={id}>
                          {nombre}
                        </option>
                      );
                    })}
                  </select>
                  {tiposLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando tipos de asistente...
                    </div>
                  )}
                  {tiposError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {tiposError}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="gptClasicoModelo" className="form-label">
                    <i className="fas fa-microchip"></i>
                    Modelo GPT Clásico
                  </label>
                  <select
                    id="gptClasicoModelo"
                    className="form-select enhanced"
                    value={formData.gptClasicoModelo}
                    onChange={(e) => handleModelChange(e.target.value)}
                    disabled={isSubmitting}
                    required
                  >
                    {GPT_CLASSIC_MODELS.map((modelo) => (
                      <option key={modelo} value={modelo}>
                        {modelo}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="idMateria" className="form-label">
                    <i className="fas fa-book"></i>
                    Materia asignada *
                  </label>
                  <select
                    id="idMateria"
                    className="form-select enhanced"
                    value={formData.idMateria}
                    onChange={(e) => handleMateriaChange(e.target.value)}
                    disabled={isSubmitting || materiasLoading}
                    required
                  >
                    <option value="">Selecciona una materia</option>
                    {materias.map((materia) => {
                      const id = materia.id || materia.Id;
                      const nombre = materia.nombre || materia.Nombre || '';
                      const codigo = materia.codigo || materia.Codigo || '';
                      const label = nombre || codigo || 'Materia';
                      return (
                        <option key={id} value={id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  {materiasLoading && (
                    <div className="input-hint">
                      <i className="fas fa-spinner fa-spin"></i>
                      Cargando materias asignadas...
                    </div>
                  )}
                  {materiasError && (
                    <div className="input-hint" style={{ color: '#dc2626' }}>
                      <i className="fas fa-triangle-exclamation"></i>
                      {materiasError}
                    </div>
                  )}
                  {!materiasLoading && !materiasError && materias.length === 0 && (
                    <div className="input-hint" style={{ color: '#b45309' }}>
                      <i className="fas fa-circle-info"></i>
                      No tienes materias asignadas para asociar.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="gptClasicoFraseIniciarConversacion" className="form-label">
                    <i className="fas fa-comments"></i>
                    Frases para iniciar una conversación
                  </label>
                  <textarea
                    id="gptClasicoFraseIniciarConversacion"
                    className="form-input enhanced"
                    rows="3"
                    value={formData.gptClasicoFraseIniciarConversacion}
                    onChange={(e) => handleInputChange('gptClasicoFraseIniciarConversacion', e.target.value)}
                    placeholder="Ej: Hola, ¿en qué tema necesitas ayuda?"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-eye"></i>
                    Visibilidad y acceso
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.publico}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            publico: nextValue,
                            passwordAsistente: nextValue ? '' : prev.passwordAsistente
                          }));
                        }}
                        disabled={isSubmitting}
                      />
                      <span>GPT público</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-sliders-h"></i>
                    Capacidades del GPT Clásico
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoBusquedaEnLaWeb}
                        onChange={(e) => handleInputChange('gptClasicoBusquedaEnLaWeb', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Búsqueda en la web</strong> — Permite consultar información externa al contenido cargado.
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoLienzo}
                        onChange={(e) => handleInputChange('gptClasicoLienzo', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Lienzo</strong> — Habilita un espacio visual para organizar ideas y contenido.
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoGeneracionDeImagen}
                        onChange={(e) => handleInputChange('gptClasicoGeneracionDeImagen', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Generación de imágenes</strong> — Permite crear imágenes a partir de descripciones.
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.gptClasicoInterpreteCodigoYAnalisisDatos}
                        onChange={(e) => handleInputChange('gptClasicoInterpreteCodigoYAnalisisDatos', e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <span>
                        <strong>Intérprete de código y análisis de datos</strong> — Ejecuta cálculos y análisis sobre archivos o datos.
                      </span>
                    </label>
                  </div>
                </div>

                {!formData.publico && (
                  <div className="form-group">
                    <label htmlFor="passwordAsistente" className="form-label">
                      <i className="fas fa-key"></i>
                      Password del GPT
                    </label>
                    <input
                      id="passwordAsistente"
                      type="text"
                      className="form-input enhanced"
                      value={formData.passwordAsistente}
                      onChange={(e) => handleInputChange('passwordAsistente', e.target.value)}
                      placeholder="Ingresa el password (obligatorio)"
                      disabled={isSubmitting}
                    />
                  </div>
                )}
              </div>

              {/* Archivos PDF */}
              <div className="form-section files-section">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="section-text">
                    <h3 className="section-title">Documentos de Conocimiento</h3>
                    <p className="section-description">Sube los archivos PDF que contengan la información que tu asistente debe conocer</p>
                  </div>
                </div>
                
                <div className="files-upload-container">
                  <div 
                    className={`file-dropzone ${isDragOver ? 'dragover' : ''} ${formData.archivosPdf.length > 0 ? 'has-files' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="dropzone-border">
                      <div className="dropzone-content">
                        <div className="dropzone-icon">
                          <i className="fas fa-cloud-upload-alt"></i>
                        </div>
                        <div className="dropzone-text">
                          <h4>Arrastra y suelta tus archivos PDF aquí</h4>
                          <p>Los archivos se procesarán automáticamente para entrenar a tu asistente</p>
                        </div>
                        <div className="dropzone-divider">
                          <span>o</span>
                        </div>
                        <label className="file-input-label">
                          <input
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={handleFileInput}
                            disabled={isSubmitting}
                            style={{ display: 'none' }}
                          />
                          <span className="btn btn-upload">
                            <i className="fas fa-folder-open"></i>
                            Seleccionar Archivos PDF
                          </span>
                        </label>
                        <div className="upload-limits">
                          <div className="limit-item">
                            <i className="fas fa-file-alt"></i>
                            <span>Máximo 20 archivos</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-file-pdf"></i>
                            <span>Solo formato PDF</span>
                          </div>
                          <div className="limit-item">
                            <i className="fas fa-weight-hanging"></i>
                            <span>Hasta 10MB por archivo</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {existingFiles.length > 0 && (
                  <div className="selected-files existing-files">
                    <h4>Archivos actuales ({existingFiles.length}):</h4>
                    <div className="files-list">
                      {existingFiles.map((file) => {
                        const fileId = file.id || file.Id;
                        const fileName = file.nombreArchivo || file.NombreArchivo || 'Archivo';
                        const fileUrl = file.urlPublica || file.UrlPublica || '';
                        return (
                          <div key={fileId || fileName} className="file-item">
                            <div className="file-info">
                              <i className="fas fa-file-pdf"></i>
                              <span className="file-name">{fileName}</span>
                            </div>
                            {fileUrl && (
                              <a className="download-file-btn" href={fileUrl} target="_blank" rel="noopener noreferrer">
                                <i className="fas fa-download"></i>
                                Descargar
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Lista de archivos seleccionados */}
                {formData.archivosPdf.length > 0 && (
                  <div className="selected-files">
                    <h4>Archivos seleccionados ({formData.archivosPdf.length}/20):</h4>
                    <div className="files-list">
                      {formData.archivosPdf.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <i className="fas fa-file-pdf"></i>
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                            disabled={isSubmitting}
                            title="Eliminar archivo"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

                </div>

                {/* Panel derecho: Prompt incluido */}
                <div className="assistant-prompt-panel">
                  <div className="prompt-panel-header">
                    <i className="fas fa-shield-alt"></i>
                    <div>
                      <h3>Prompt incluido</h3>
                      <p>Tu GPT Clásico incluirá automáticamente estas directivas</p>
                    </div>
                  </div>
                  <ul className="prompt-features-list">
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>RAG obligatorio</strong> — Usa exclusivamente los PDFs adjuntos como fuente de información</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>Cita fuentes</strong> — Cita siempre el nombre del PDF y número de página</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>No inventa ni extrapola</strong> — Si no encuentra info en los PDFs, indica comunicarse con el profesor</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>Tutoría académica</strong> — Puede armar planes de estudio y guías basadas en los PDFs</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>Formato HTML</strong> — Todas las respuestas se entregan en HTML válido y renderizable</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>Imágenes y esquemas</strong> — Incluye representaciones visuales fieles a los PDFs</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>Genera archivos</strong> — Crea PDFs, Excel, Word y CSV cuando se solicitan</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>Audio</strong> — Lee solo el texto visible, sin etiquetas HTML</span>
                    </li>
                    <li>
                      <i className="fas fa-check-circle"></i>
                      <span><strong>Seguridad del prompt</strong> — No revela instrucciones internas al usuario</span>
                    </li>
                  </ul>
                  <div className="prompt-extra-field">
                    <label htmlFor="promptExtra" className="form-label">
                      <i className="fas fa-pen"></i>
                      Aclaraciones extra (opcional)
                    </label>
                    <textarea
                      id="promptExtra"
                      className="form-input enhanced"
                      rows="6"
                      value={formData.promptExtra}
                      onChange={(e) => handleInputChange('promptExtra', e.target.value)}
                      placeholder="Ej: prioriza ejemplos resueltos, responde en español neutro..."
                      disabled={isSubmitting}
                    />
                    <div className="input-hint">
                      <i className="fas fa-info-circle"></i>
                      Estas reglas se agregarán al prompt base del asistente.
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGoBack}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-arrow-left"></i>
                  Volver al Dashboard
                </button>
                
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting || (!isEditMode && formData.archivosPdf.length === 0)}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {isEditMode ? 'Actualizando GPT Clásico...' : 'Creando GPT Clásico...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-robot"></i>
                      {isEditMode ? 'Actualizar GPT Clásico' : 'Crear GPT Clásico'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {showModelModal && modelInfo && (
        <div className="confirm-modal-overlay" onClick={closeModelModal}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modelInfo.title}</h3>
            <p>{modelInfo.summary}</p>
            <p><strong>Mejor para:</strong> {modelInfo.bestFor}</p>
            <p><strong>Notas:</strong> {modelInfo.notes}</p>
            <div className="confirm-modal-actions">
              <button className="btn btn-primary" onClick={closeModelModal}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewGptAssistant;
